import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Icon } from '../../../components/ui';
import { useToast } from '../../../contexts/ToastContext';
import { useAuth } from '../../../contexts/AuthContext';
import RichTextEditor from '../../../components/RichTextEditor';
import { usePhone } from '../PhoneContext';
import { PopoutPhoneHost } from './PopoutPhone';
import { addCustomerNote } from '../api';
import { CallTimer } from './CallTimer';

/**
 * CallBar — the persistent, docked call surface (the feature's
 * signature). Rendered once globally; sits at the bottom-start and
 * only appears when there's an outbound/active call. Incoming calls
 * render the fuller IncomingCallCard instead.
 *
 * States it shows: dialing → active/held. Buttons adapt per state.
 */

const stateLabel: Record<string, string> = {
  dialing: 'מחייג…',
  ringing: 'מצלצל…',
  active: '',
  held: 'בהמתנה',
  ended: 'הסתיים',
};

export const CallBar: React.FC = () => {
  const {
    call, config, hangup, toggleMute, toggleHold, addParticipant, openDialer, micGain, setMicGain,
    micDeviceId, microphones, setMicDevice, refreshMicrophones,
    speakerDeviceId, speakers, speakerSupported, setSpeaker, refreshSpeakers,
  } = usePhone();
  const navigate = useNavigate();
  const { isAgent } = useAuth();
  const toast = useToast();
  const [notesOpen, setNotesOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addNumber, setAddNumber] = useState('');
  const [note, setNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [volOpen, setVolOpen] = useState(false);

  // Incoming/ringing is handled by IncomingCallCard, not here.
  if (!call || call.direction === 'inbound' && call.state === 'ringing') return null;

  const cust = call.party?.customer;
  const name = cust?.fullName || call.number;
  const company = cust?.company?.name;
  const isActive = call.state === 'active';
  const isHeld = call.state === 'held';
  // 019 does hold over SIP for every call; Twilio can only do it inside a
  // conference, which outbound calls no longer use.
  const supportsConference = config?.provider !== 'twilio' || call.direction === 'inbound';
  const isEnded = call.state === 'ended';

  // Empty-content guard: TipTap emits "<p></p>" for a blank editor.
  const noteIsEmpty = !note.replace(/<[^>]*>/g, '').trim();

  const saveNote = async () => {
    if (!cust || noteIsEmpty) return;
    setSavingNote(true);
    try {
      await addCustomerNote(cust.id, note);
      toast.success('ההערה נשמרה על הלקוח');
      setNote('');
      setNotesOpen(false);
    } catch {
      toast.error('שמירת ההערה נכשלה');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div
      className="fixed bottom-4 end-4 z-[1200] w-[min(540px,calc(100vw-2rem))] animate-slide-up"
      role="region"
      aria-label="שיחה פעילה"
    >
      <div className="flex items-center gap-3 rounded-2xl bg-slate-900 border border-slate-700 text-slate-100 px-4 py-3 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.6)]">
        {/* Avatar with a live ring when connected */}
        <div className="relative shrink-0">
          <Avatar name={name} size="md" />
          {isActive && (
            <span className="absolute -inset-1 rounded-2xl border-2 border-emerald-400/70 animate-ping-slow pointer-events-none" />
          )}
        </div>

        {/* Who + state. basis-0 plus a min-width floor keeps the name
            readable instead of letting the control row squeeze it down
            to an ellipsis. */}
        <button
          className="flex-1 basis-0 min-w-[7rem] text-start"
          // A נציג טלפוני has no access to the customer record — the dialer
          // shows them who is calling and nothing more — so the name is not a
          // link for them rather than a control that bounces back to /calls.
          disabled={isAgent}
          onClick={() => {
            if (isAgent) return;
            cust
              ? navigate(`/customers/${cust.id}/activity`)
              : navigate(`/customers?tab=customers&createPhone=${encodeURIComponent(call.number)}`);
          }}
          title={isAgent ? undefined : (cust ? 'פתח כרטיס לקוח' : 'צור לקוח מהמספר')}
        >
          <div className="font-semibold text-sm truncate">
            {name}{company ? <span className="text-slate-400 font-normal"> · {company}</span> : null}
          </div>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-400">
            {isActive ? (
              <>
                <span className="flex items-end gap-[2px] h-3" aria-hidden>
                  {[6, 12, 8, 14, 7].map((h, i) => (
                    <span key={i} className="w-[2.5px] rounded-full bg-emerald-400 animate-wave"
                      style={{ height: h, animationDelay: `${i * 0.12}s` }} />
                  ))}
                </span>
                <CallTimer since={call.answeredAt} className="tabular-nums text-emerald-400 font-semibold" />
                {/* Recording indicator — every answered call is recorded. */}
                <span className="inline-flex items-center gap-1 text-red-400" title="השיחה מוקלטת">
                  <span className="size-2 rounded-full bg-red-500 animate-pulse" />
                  <span className="text-[10px] font-semibold">REC</span>
                </span>
              </>
            ) : (
              <span className={[
                'inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold',
                isEnded ? 'bg-slate-700 text-slate-300'
                : isHeld ? 'bg-amber-500/16 text-amber-300'
                : 'bg-blue-500/16 text-blue-300',
              ].join(' ')}>
                {stateLabel[call.state]}
              </span>
            )}
            {!isActive && <span className="tabular-nums truncate" dir="ltr">{call.number}</span>}
          </div>
        </button>

        {/* Controls */}
        <div className="flex items-center gap-1.5 shrink-0">
          {isActive || isHeld ? (
            <>
              <IconBtn on={call.muted} icon={call.muted ? 'mic_off' : 'mic'} title="השתקה" onClick={toggleMute} accent />
              {/* Outbound volume: how loud the customer hears the rep. Popover
                  slider; the level persists across calls. */}
              <div className="relative">
                <IconBtn
                  on={volOpen}
                  icon={micGain >= 1.05 ? 'volume_up' : 'volume_down'}
                  title="הגדרות מיקרופון (מה שהלקוח שומע)"
                  onClick={() => { const n = !volOpen; setVolOpen(n); if (n) { refreshMicrophones(); refreshSpeakers(); } }}
                  accent
                />
                {volOpen && (
                  <div className="absolute bottom-full mb-2 right-1/2 translate-x-1/2 z-10 w-64 rounded-2xl bg-slate-900 border border-slate-700 p-3 shadow-xl animate-slide-up">
                    {/* Which microphone to use. The browser defaults to the
                        system mic (laptop's), not the headset — the field
                        cause of reps sounding far/quiet. */}
                    <div className="mb-3">
                      <div className="text-slate-300 text-[11px] font-semibold mb-1">מיקרופון</div>
                      <select
                        value={micDeviceId || ''}
                        onChange={(e) => setMicDevice(e.target.value || null)}
                        className="w-full rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-xs px-2 py-1.5 outline-none focus:border-primary"
                        aria-label="בחירת מיקרופון"
                      >
                        <option value="">ברירת מחדל של המערכת</option>
                        {microphones.map((m) => (
                          <option key={m.deviceId} value={m.deviceId}>{m.label}</option>
                        ))}
                      </select>
                      <div className="text-[10px] text-slate-500 mt-1">מתחלף מיד, גם באמצע שיחה</div>
                    </div>
                    {/* Which speaker plays the customer to the rep (setSinkId).
                        Applies live; hidden on browsers without setSinkId. */}
                    {speakerSupported && (
                      <div className="mb-3">
                        <div className="text-slate-300 text-[11px] font-semibold mb-1">רמקול</div>
                        <select
                          value={speakerDeviceId || ''}
                          onChange={(e) => setSpeaker(e.target.value || null)}
                          className="w-full rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-xs px-2 py-1.5 outline-none focus:border-primary"
                          aria-label="בחירת רמקול"
                        >
                          <option value="">ברירת מחדל של המערכת</option>
                          {speakers.map((s) => (
                            <option key={s.deviceId} value={s.deviceId}>{s.label}</option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2 text-slate-300 text-[11px] font-semibold">
                      <span>עוצמת המיקרופון</span>
                      <span className="tabular-nums text-primary">{Math.round(micGain * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min={1}
                      max={2.5}
                      step={0.1}
                      value={micGain}
                      onChange={(e) => setMicGain(Number(e.target.value))}
                      className="w-full accent-primary cursor-pointer"
                      aria-label="עוצמת המיקרופון"
                    />
                    <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
                      <span>רגיל</span>
                      <button
                        onClick={() => setMicGain(1.5)}
                        className="text-primary hover:underline"
                        title="אפס לברירת מחדל"
                      >
                        אפס (150%)
                      </button>
                      <span>חזק</span>
                    </div>
                  </div>
                )}
              </div>
              {/* Hold and 3-way need a conference. On Twilio only inbound
                  queue calls run in one — outbound is a direct <Dial>, which
                  is cheaper and can't strand a ringing callee — so hide both
                  controls there rather than show a button that errors. 019
                  keeps hold via SIP as before. */}
              {supportsConference && (
                <IconBtn on={isHeld} icon="pause" title="החזקה" onClick={toggleHold} accent />
              )}
              {config?.provider === 'twilio' && supportsConference && (
                <div className="relative">
                  <IconBtn on={addOpen} icon="group_add" title="הוסף משתתף לשיחה" onClick={() => setAddOpen(o => !o)} accent />
                  {addOpen && (
                    <div className="absolute bottom-full mb-2 right-1/2 translate-x-1/2 z-10 w-56 rounded-2xl bg-slate-900 border border-slate-700 p-3 shadow-xl animate-slide-up">
                      <div className="text-slate-300 text-[11px] font-semibold mb-1.5">הוסף משתתף לשיחה</div>
                      <input
                        type="tel"
                        inputMode="tel"
                        placeholder="050-1234567"
                        value={addNumber}
                        onChange={(e) => setAddNumber(e.target.value)}
                        className="w-full rounded-lg bg-slate-800 border border-slate-700 text-slate-100 text-sm px-2 py-1.5 outline-none focus:border-primary"
                      />
                      <button
                        onClick={async () => { if (!addNumber.trim()) return; await addParticipant(addNumber.trim()); setAddNumber(''); setAddOpen(false); }}
                        className="mt-2 w-full rounded-lg bg-primary hover:bg-primary-700 text-white text-xs font-semibold py-1.5 transition"
                      >
                        חייג וצרף
                      </button>
                    </div>
                  )}
                </div>
              )}
              <IconBtn icon="dialpad" title="חייגן" onClick={openDialer} />
              {/* Pops the call out into a small always-on-top window, so a
                  rep working in another tab can still hang up. */}
              <PopoutPhoneHost />
              {cust && (
                <IconBtn on={notesOpen} icon="edit_note" title="הערה לשיחה" onClick={() => setNotesOpen(o => !o)} accent />
              )}
            </>
          ) : null}
          <button
            onClick={hangup}
            className="size-10 rounded-xl bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition hover:-translate-y-0.5"
            title={call.direction === 'outbound' && !isActive ? 'ביטול' : 'ניתוק'}
            aria-label="ניתוק שיחה"
          >
            <Icon name="call_end" size={20} />
          </button>
        </div>
      </div>

      {/* In-call note — rich editor on a light surface, saved to the
          customer's notes. */}
      {notesOpen && cust && (
        <div className="mt-2 rounded-2xl bg-slate-900 border border-slate-700 p-3 animate-slide-up">
          <div className="flex items-center gap-2 mb-2 text-slate-300 text-xs font-semibold">
            <Icon name="edit_note" size={16} /> הערה על {cust.fullName}
          </div>
          {/* Light editing surface: white background, black text. */}
          <div className="rounded-lg bg-white text-slate-900 overflow-hidden">
            <RichTextEditor
              value={note}
              onChange={setNote}
              placeholder="כתוב הערה מהשיחה…"
              height={180}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-[11px] text-slate-500">נשמר על כרטיס הלקוח</span>
            <button
              onClick={saveNote}
              disabled={noteIsEmpty || savingNote}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary-700 disabled:opacity-40 text-white text-xs font-semibold px-3 py-1.5 transition"
            >
              <Icon name={savingNote ? 'progress_activity' : 'check'} size={14} className={savingNote ? 'animate-spin' : ''} />
              שמור
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const IconBtn: React.FC<{ icon: string; title: string; onClick: () => void; on?: boolean; accent?: boolean }> =
({ icon, title, onClick, on, accent }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-pressed={on}
    className={[
      'size-10 rounded-xl flex items-center justify-center transition hover:-translate-y-0.5',
      on && accent ? 'bg-orange-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700',
    ].join(' ')}
  >
    <Icon name={icon} size={20} />
  </button>
);
